import "./shared/IntakeForm.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import KaboodleToaster from "components/common/KaboodleToaster";
import KaboodleModal from "components/Modals/KaboodleModal";
import Tooltip from "components/Tooltip/Tooltip";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { FEATURE_FLAGS } from "constants/FeatureFlags";
import { useIntakeToken } from "utils/context";
import { useConstants, useCurrentUser } from "utils/hooks";

import IntakeHeader from "./components/HeaderIntake";
import IntakeProgressBar from "./components/IntakeProgressBar";
import SubmissionOverlay from "./components/SubmissionOverlay";
import SubmissionSuccessModal from "./components/SubmissionSuccessModal";
import { useIntakeNavigation } from "./hooks/useIntakeNavigation";
import { useIntakeSubmission } from "./hooks/useIntakeSubmission";
import {
  fieldValidationMap,
  useIntakeValidation,
} from "./hooks/useIntakeValidation";
import { useUploadNavigationBlock } from "./hooks/useUploadNavigationBlock";
import AccidentInfoIntakeForm from "./sections/AccidentInfoIntakeForm";
import ClaimantIntakeForm from "./sections/ClaimantIntakeForm";
import DocumentUploadAndAdjusterIntake from "./sections/DocumentUploadAndAdjusterIntake";
import type { DocumentUploadIntakeRef } from "./sections/DocumentUploadIntake";
import EmployerIntakeForm from "./sections/EmployerIntakeForm";
import type { FormData } from "./shared/IntakeTypes";
import { DEFAULT_FORM_VALUES } from "./shared/utils/defaultFormValues";
import { computeIsLibertyUser } from "./shared/utils/isLibertyUser";

const LIBERTY_REFERRAL_FIELDS = [
  "grs_or_legacy_rcg",
  "rcg_carrier",
  "segmentation_type",
  "referral_type",
  "wcrc_case_id",
] as const;

// Stable fallback for dates_of_injury: DEFAULT_FORM_VALUES has no entry for
// it (only ClaimantIntakeForm registers it, inside a useEffect that runs
// after first mount), so useWatch can return undefined briefly. A module-
// level constant, rather than an inline `?? []`, keeps the fallback's
// reference stable across renders so it doesn't itself defeat memoization.
const EMPTY_DATES_OF_INJURY: FormData["dates_of_injury"] = [];

/*
this is the main intake form component that handles the navigation and submission of the intake form
*/
const ClientIntakeForm = () => {
  const [page, setPage] = useState(0);
  const [showSkipConfirmModal, setShowSkipConfirmModal] = useState(false);
  const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const methods = useForm<FormData>({
    mode: "onBlur",
    defaultValues: DEFAULT_FORM_VALUES,
  });
  const documentUploadRef = useRef<DocumentUploadIntakeRef>(null);
  const { reset, setValue, control } = methods;
  // useWatch (not methods.watch() called inline) so this participates in
  // React's normal dependency tracking -- calling methods.watch() directly
  // inside a dependency array creates a fresh, uncoordinated subscription
  // on every render and can read a stale value relative to when the
  // dependency itself was evaluated.
  const datesOfInjury =
    useWatch({ control, name: "dates_of_injury" }) ?? EMPTY_DATES_OF_INJURY;
  const currentUser = useCurrentUser();
  const { intakeToken, intakeUser } = useIntakeToken();
  const { constants } = useConstants();

  // Liberty Mutual/Helmsman staff get an extra card on the wizard's first
  // page for referral fields. See computeIsLibertyUser for the full
  // reasoning behind the two login paths and the org/user_type check.
  const isStaffLibertyFeatureEnabled = Boolean(
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.LIBERTY_REFERRAL_FIELDS_ENABLED],
  );

  const isLibertyUser = useMemo(
    () =>
      computeIsLibertyUser({
        currentUser,
        intakeToken,
        intakeUser,
        isStaffLibertyFeatureEnabled,
      }),
    [isStaffLibertyFeatureEnabled, currentUser, intakeToken, intakeUser],
  );

  const { handleSubmitError, showFieldValidationErrors } =
    useIntakeValidation();
  const {
    isSubmitting,
    submissionMessage,
    isSubmissionComplete,
    showSuccessModal,
    submissionIdentifier,
    submitForm,
    handleCompleteAnother,
    initializeSubmission,
  } = useIntakeSubmission();
  const { handleNext, handlePrev } = useIntakeNavigation({
    methods,
    page,
    setPage,
  });

  // initialize form with default values, or empty
  useEffect(() => {
    reset(DEFAULT_FORM_VALUES);
  }, [reset]);

  const handleFieldChange = useCallback(
    async (
      fieldName: string,
      value: string | boolean | Array<{ value: string; label: string }>,
    ): Promise<void> => {
      setValue(fieldName as keyof FormData, value as FormData[keyof FormData]);
    },
    [setValue],
  );

  const handleUploadStateChange = useCallback(
    (uploading: boolean, count: number) => {
      setIsUploading(uploading);
      setUploadingCount(count);
    },
    [],
  );

  // prevent navigation when files are uploading
  useUploadNavigationBlock({
    isUploading,
    uploadingCount,
  });

  const handleCompleteAnotherForm = useCallback(() => {
    // reset form to default values
    reset(DEFAULT_FORM_VALUES);
    // reset page to first page
    setPage(0);
    // reset submission state
    handleCompleteAnother();
  }, [reset, setPage, handleCompleteAnother]);

  // form submission with final validation
  const onSubmit: SubmitHandler<FormData> = async (data) => {
    // only allow submission on the last page
    if (!canSubmit) return;

    // check if files are still uploading
    if (documentUploadRef.current?.isUploading()) {
      const uploadCount = documentUploadRef.current?.getUploadingCount() || 0;
      KaboodleToaster.error(
        `Please wait for ${uploadCount} file${uploadCount !== 1 ? "s" : ""} to finish uploading before submitting.`,
      );
      return;
    }

    // final validation check before submission
    const finalValidationFields =
      fieldValidationMap.flat() as (keyof FormData)[];

    const isFormValid = await methods.trigger(finalValidationFields);

    if (!isFormValid) {
      const errors = methods.formState.errors;
      const errorCount = Object.keys(errors).length;

      if (errorCount > 0) {
        const toastMessage = `Please fix ${errorCount} validation error${
          errorCount !== 1 ? "s" : ""
        } before submitting the form.`;
        KaboodleToaster.error(toastMessage);
        return;
      }
    }

    await submitForm(data);
  };

  // helper function to check if DOI has meaningful content
  const hasMeaningfulDOI = useCallback(() => {
    // check if there's any DOI with actual content (not just placeholders)
    const hasMeaningful = datesOfInjury.some((doi) => {
      // consider it meaningful if:
      // - has a valid date (not empty and not the millennium placeholder)
      // - has at least one claim number with actual content (not empty and not placeholder)
      const hasRealDate =
        doi.date && doi.date.trim() !== "" && doi.date !== "01/01/2000";
      const hasRealClaimNumber = doi.claim_numbers?.some(
        (claim) =>
          claim.number &&
          claim.number.trim() !== "" &&
          claim.number !== "INTAKE-PLACEHOLDER-001",
      );

      // if we have either a real date or real claim number, consider it meaningful
      return hasRealDate && hasRealClaimNumber;
    });

    return hasMeaningful;
  }, [datesOfInjury]);

  // determine which pages to show based on DOI content
  const formPages = useMemo(() => {
    // base form sections (before DOIs) - use keys to prevent remounting.
    // Liberty Mutual/Helmsman submitters get an extra card at the top of
    // the first page (DocumentUploadAndAdjusterIntake), not an extra page
    // -- everyone else's wizard is completely unaffected.
    const basePages = [
      <DocumentUploadAndAdjusterIntake
        key="page-upload"
        ref={documentUploadRef}
        handleFieldChange={handleFieldChange}
        intakeIdentifier={submissionIdentifier}
        onUploadStateChange={handleUploadStateChange}
        initializeSubmission={initializeSubmission}
        isLibertyUser={isLibertyUser}
      />,
      <ClaimantIntakeForm
        key="page-claimant"
        handleFieldChange={handleFieldChange}
      />,
      <EmployerIntakeForm
        key="page-employer"
        handleFieldChange={handleFieldChange}
      />,
    ];

    // only add DOI pages if there's meaningful DOI data
    if (hasMeaningfulDOI()) {
      // DOI pages (one per date of injury). isLibertyUser is threaded
      // through here on purpose -- Claim Notes must render beside
      // Accident Information (KAB-3359 ticket requirement, non-negotiable
      // per explicit product direction; do not move it to another page,
      // e.g. Claimant Information, even to work around the caveat below).
      // Known, accepted tradeoff: since this page only mounts when
      // hasMeaningfulDOI() is true, Claim Notes is unreachable via
      // "Skip & Submit" before any DOI has meaningful data, same as
      // accident_description and the other DOI-page fields.
      const doiPages = datesOfInjury.map((_, index) => (
        <AccidentInfoIntakeForm
          key={`doi-${index}`}
          handleFieldChange={handleFieldChange}
          doiIndex={index}
          isLibertyUser={isLibertyUser}
        />
      ));

      return [...basePages, ...doiPages];
    }

    return basePages;
  }, [
    isLibertyUser,
    hasMeaningfulDOI,
    handleFieldChange,
    handleUploadStateChange,
    datesOfInjury,
    submissionIdentifier,
  ]);

  // Only show submit button on the actual last page
  const canSubmit = formPages.length > 0 && formPages.length === page + 1;

  // function to show confirmation modal before skip and submit
  const handleSkipAndSubmitClick = useCallback(() => {
    // check if files are still uploading
    if (documentUploadRef.current?.isUploading()) {
      const uploadCount = documentUploadRef.current?.getUploadingCount() || 0;
      KaboodleToaster.error(
        `Please wait for ${uploadCount} file${uploadCount !== 1 ? "s" : ""} to finish uploading before submitting.`,
      );
      return;
    }
    setShowSkipConfirmModal(true);
  }, []);

  // function to handle confirmed skip and submit
  const handleConfirmSkipAndSubmit = useCallback(async () => {
    setShowSkipConfirmModal(false);

    if (isLibertyUser) {
      const isValid = await methods.trigger([...LIBERTY_REFERRAL_FIELDS]);
      if (!isValid) {
        showFieldValidationErrors(
          [...LIBERTY_REFERRAL_FIELDS],
          methods.formState.errors,
        );
        return;
      }
    }

    const currentData = methods.getValues();

    // submitForm already handles all defaults, so just pass the current data
    await submitForm(currentData);
  }, [isLibertyUser, methods, showFieldValidationErrors, submitForm]);

  // function to handle cancel skip and submit
  const handleCancelSkipAndSubmit = useCallback(() => {
    setShowSkipConfirmModal(false);
  }, []);

  // function to show confirmation modal before normal submit
  const handleSubmitClick = useCallback(() => {
    // check if files are still uploading
    if (documentUploadRef.current?.isUploading()) {
      const uploadCount = documentUploadRef.current?.getUploadingCount() || 0;
      KaboodleToaster.error(
        `Please wait for ${uploadCount} file${uploadCount !== 1 ? "s" : ""} to finish uploading before submitting.`,
      );
      return;
    }
    setShowSubmitConfirmModal(true);
  }, []);

  // function to handle confirmed normal submit
  const handleConfirmSubmit = useCallback(async () => {
    setShowSubmitConfirmModal(false);
    // use handleSubmit to get proper validation and error handling
    methods.handleSubmit(onSubmit, handleSubmitError)();
  }, [methods, onSubmit, handleSubmitError]);

  // function to handle cancel normal submit
  const handleCancelSubmit = useCallback(() => {
    setShowSubmitConfirmModal(false);
  }, []);

  // footer navigation buttons
  const buttonPanel = (
    <div className="form-actions">
      <div className="form-actions-left">
        <Button
          type="button"
          disabled={isSubmitting || page === 0}
          className="submit-button"
          onClick={handlePrev}
        >
          Previous
        </Button>
        {canSubmit ? (
          isUploading ? (
            <Tooltip
              content={`Wait for ${uploadingCount} file${uploadingCount !== 1 ? "s" : ""} to finish uploading`}
              place="top"
            >
              <Button
                type="button"
                disabled={isSubmitting || isUploading}
                className="submit-button"
                onClick={handleSubmitClick}
              >
                {isSubmitting ? "Submitting..." : "Submit Intake Form"}
              </Button>
            </Tooltip>
          ) : (
            <Button
              type="button"
              disabled={isSubmitting}
              className="submit-button"
              onClick={handleSubmitClick}
            >
              {isSubmitting ? "Submitting..." : "Submit Intake Form"}
            </Button>
          )
        ) : (
          <>
            <Button
              type="button"
              disabled={isSubmitting}
              className="submit-button"
              onClick={() => {
                handleNext(fieldValidationMap[page] as (keyof FormData)[]);
              }}
            >
              Next
            </Button>
            {isUploading ? (
              <Tooltip
                content={`Wait for ${uploadingCount} file${uploadingCount !== 1 ? "s" : ""} to finish uploading`}
                place="top"
              >
                <Button
                  type="button"
                  disabled={isSubmitting || isUploading}
                  className="skip-submit-button"
                  onClick={handleSkipAndSubmitClick}
                >
                  Skip & Submit
                </Button>
              </Tooltip>
            ) : (
              <Button
                type="button"
                disabled={isSubmitting}
                className="skip-submit-button"
                onClick={handleSkipAndSubmitClick}
              >
                Skip & Submit
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );

  const progressPercentage =
    formPages.length > 0 ? (page / formPages.length) * 100 : 0;

  return (
    <div className="intake-form-page dashboard">
      <IntakeHeader isUploading={isUploading} uploadingCount={uploadingCount} />
      <div className="main-content intake-content">
        <h1 className="dashboard-title">Client Intake Form</h1>
        <IntakeProgressBar
          progress={progressPercentage}
          showPercentage={true}
          className="allan-koba"
          height="12px"
        />
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            {formPages.map((pageContent, index) => (
              <div
                key={`page-${index}`}
                className="grid-layout gl-2"
                style={{ display: index === page ? "grid" : "none" }}
              >
                {pageContent}
              </div>
            ))}
            {buttonPanel}
          </form>
        </FormProvider>
      </div>
      <KaboodleToaster.ToastContainer />

      <SubmissionOverlay
        isVisible={isSubmitting}
        message={submissionMessage}
        isComplete={isSubmissionComplete}
      />

      <SubmissionSuccessModal
        isVisible={showSuccessModal}
        onCompleteAnother={handleCompleteAnotherForm}
      />

      <KaboodleModal
        isOpen={showSkipConfirmModal}
        onClose={handleCancelSkipAndSubmit}
        footer={
          <div className="flex jc-c">
            <Button
              className="submit-button"
              onClick={handleConfirmSkipAndSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Intake Form"}
            </Button>
            <Button onClick={handleCancelSkipAndSubmit}>Cancel</Button>
          </div>
        }
      >
        <Card heading="Skip and Submit Form">
          {isLibertyUser ? (
            <p>
              Liberty Mutual referral fields on the first page must be completed
              before submitting, even when skipping the rest of the form.
            </p>
          ) : (
            <p>
              Our team will review the intake form and fill in the missing
              information.
            </p>
          )}
          <p>
            <strong>Are you sure you want to proceed?</strong>
          </p>
        </Card>
      </KaboodleModal>

      <KaboodleModal
        isOpen={showSubmitConfirmModal}
        onClose={handleCancelSubmit}
        footer={
          <div className="flex jc-c">
            <Button
              className="submit-button"
              onClick={handleConfirmSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Intake Form"}
            </Button>
            <Button onClick={handleCancelSubmit}>Cancel</Button>
          </div>
        }
      >
        <Card heading="Submit Intake Form">
          <p>
            Once submitted, our team will begin processing this form as soon as
            possible.
          </p>
          <p></p>
        </Card>
      </KaboodleModal>
    </div>
  );
};

export default ClientIntakeForm;
